import { useVirtualizer } from "@tanstack/react-virtual";
import { useEffect, useRef, type ReactNode } from "react";

/** Lists are virtualised above 100 rows (AGENTS.md); a group or space source has no member limit. */
export const VIRTUAL_ABOVE = 100;

/** A fixed-height list that draws only the rows in view, and scrolls `active` into view when it changes. */
export function VirtualList<T>({
  items,
  rowKey,
  row,
  rowHeight,
  label,
  active,
  className = "",
}: {
  items: T[];
  rowKey: (item: T) => string;
  row: (item: T) => ReactNode;
  rowHeight: number;
  label?: string;
  active?: number;
  className?: string;
}) {
  const scroller = useRef<HTMLDivElement>(null);
  // TanStack Virtual returns new functions on every render, which the React Compiler cannot memoise; that is fine.
  // eslint-disable-next-line react-hooks/incompatible-library
  const virtual = useVirtualizer({
    count: items.length,
    getScrollElement: () => scroller.current,
    estimateSize: () => rowHeight,
    overscan: 12,
  });
  const virtualRef = useRef(virtual);
  useEffect(() => {
    virtualRef.current = virtual;
  });
  // Only when the active row changes, so the user's own scrolling is left alone.
  useEffect(() => {
    if (active !== undefined && active >= 0) virtualRef.current.scrollToIndex(active, { align: "auto" });
  }, [active]);
  return (
    <div ref={scroller} className="min-h-0 flex-1 overflow-auto" style={{ maxHeight: "60vh" }}>
      <ul aria-label={label} style={{ height: virtual.getTotalSize(), position: "relative" }}>
        {virtual.getVirtualItems().map((v) => {
          const item = items[v.index];
          if (item === undefined) return null;
          return (
            <li
              key={rowKey(item)}
              aria-posinset={v.index + 1}
              aria-setsize={items.length}
              className={`absolute inset-x-0 flex items-center gap-2 ${className}`}
              style={{ height: rowHeight, transform: `translateY(${v.start}px)` }}
            >
              {row(item)}
            </li>
          );
        })}
      </ul>
    </div>
  );
}
